/**
 * Storageキー定義
 */
import { DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA } from "@/constants/account";
import { AddNewPropertyFormSchema } from "@/types/addNewProperty";
import { AccountRegisterFormData } from "@/validations/account";
import { PropertyHolder } from "@/view_models/atoms";

import { clearExpoRegistration } from "./expoRegistration";

const SELECTED_PROPERTY_ID_STORAGE_KEY = "SELECTED_PROPERTY_ID";
const PROPERTY_FORM_STORAGE_KEY = "PROPERTY_FORM";
const SELECTED_PROPERTY_STORAGE_KEY = "SELECTED_PROPERTY";
const APP_DEVICE_TOKEN = "APP_DEVICE_TOKEN";
const PROPERTY_TEMPORARY_FORM_STORAGE_KEY = "PROPERTY_TEMPORARY_FORM";
const PROPERTY_TEMPORARY_STEP_STORAGE_KEY = "PROPERTY_TEMPORARY_STEP";

// ログアウト時に呼ぶ。セッション内の一時保存に加え、端末に残すエキスポ導線の目印も次のアカウントへ持ち越さない
export const clearStorage = (): void => {
  sessionStorage.clear();
  clearExpoRegistration();
};

export const getStorageSelectedPropertyId = (): number => {
  return Number(sessionStorage.getItem(SELECTED_PROPERTY_ID_STORAGE_KEY));
};

export const setStorageSelectedPropertyId = (value: number) => {
  sessionStorage.setItem(SELECTED_PROPERTY_ID_STORAGE_KEY, String(value));
};

export const removeStorageSelectedPropertyId = () => {
  sessionStorage.removeItem(SELECTED_PROPERTY_ID_STORAGE_KEY);
};

export const getStoragePropertyForm = (): Object => {
  return JSON.parse(sessionStorage.getItem(PROPERTY_FORM_STORAGE_KEY));
};

export const setStoragePropertyForm = (value: Object) => {
  sessionStorage.setItem(PROPERTY_FORM_STORAGE_KEY, JSON.stringify(value));
};
export const setStoragePropertyFormString = (value: string) => {
  sessionStorage.setItem(PROPERTY_FORM_STORAGE_KEY, value);
};

export const removeStoragePropertyForm = () => {
  sessionStorage.removeItem(PROPERTY_FORM_STORAGE_KEY);
};

export const getStorageSelectedProperty = (): PropertyHolder => {
  return JSON.parse(sessionStorage.getItem(SELECTED_PROPERTY_STORAGE_KEY));
};

export const setStorageSelectedProperty = (value: PropertyHolder) => {
  sessionStorage.setItem(SELECTED_PROPERTY_STORAGE_KEY, JSON.stringify(value));
};

export const removeStorageSelectedProperty = () => {
  sessionStorage.removeItem(SELECTED_PROPERTY_STORAGE_KEY);
};

export const getAppDeviceToken = (): string => {
  return sessionStorage.getItem(APP_DEVICE_TOKEN);
};

export const setAppDeviceToken = (value: string) => {
  sessionStorage.setItem(APP_DEVICE_TOKEN, value);
};

export const removeAppDeviceToken = () => {
  sessionStorage.removeItem(APP_DEVICE_TOKEN);
};

export const getStoragePropertyTemporaryForm = (): Object => {
  return JSON.parse(sessionStorage.getItem(PROPERTY_TEMPORARY_FORM_STORAGE_KEY));
};

export const setStoragePropertyTemporaryForm = (value: string) => {
  sessionStorage.setItem(PROPERTY_TEMPORARY_FORM_STORAGE_KEY, value);
};

export const removePropertyTemporaryForm = () => {
  sessionStorage.removeItem(PROPERTY_TEMPORARY_FORM_STORAGE_KEY);
};

export const getStoragePropertyTemporaryStep = (): number => {
  return Number(sessionStorage.getItem(PROPERTY_TEMPORARY_STEP_STORAGE_KEY));
};

export const setStoragePropertyTemporaryStep = (value: number) => {
  sessionStorage.setItem(PROPERTY_TEMPORARY_STEP_STORAGE_KEY, String(value));
};

export const removeStoragePropertyTemporaryStep = () => {
  sessionStorage.removeItem(PROPERTY_TEMPORARY_STEP_STORAGE_KEY);
};

// アカウント登録
const ACCOUNT_REGISTER_KEY = "ACCOUNT_REGISTER";
const ACCOUNT_REGISTER_USER_ID_KEY = "ACCOUNT_REGISTER_USER_ID";

export const setStorageAccountRegistrationState = (value: AccountRegisterFormData, userId?: number): void => {
  try {
    if (typeof window === "undefined" || typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(ACCOUNT_REGISTER_KEY, JSON.stringify(value));
    if (userId != null) sessionStorage.setItem(ACCOUNT_REGISTER_USER_ID_KEY, String(userId));
  } catch (_) {
    // 保存拒否や容量超過でも入力は続けられる。半端な所有者情報は残さない。
    removeStorageAccountRegistrationState();
  }
};

export const getStorageAccountRegistrationUserId = (): number | null => {
  try {
    if (typeof window === "undefined" || typeof sessionStorage === "undefined") return null;
    const raw = sessionStorage.getItem(ACCOUNT_REGISTER_USER_ID_KEY);
    if (raw === null) return null;
    const id = Number(raw);
    // 不正値・読み取り拒否は所有者不明の匿名回答として扱わない。
    return Number.isSafeInteger(id) && id > 0 ? id : Number.NaN;
  } catch (_) {
    return Number.NaN;
  }
};

export const getStorageAccountRegistrationState = (userId?: number): Partial<AccountRegisterFormData> | null => {
  try {
    if (typeof window === "undefined" || typeof sessionStorage === "undefined") return null;
    const owner = getStorageAccountRegistrationUserId();
    if (owner !== null && owner !== userId) return null;
    const saved = JSON.parse(sessionStorage.getItem(ACCOUNT_REGISTER_KEY));
    if (!saved || typeof saved !== "object" || Array.isArray(saved)) return null;
    // 未入力・旧版の部分データを受け入れ、型が違う項目だけを除外する。
    const restored: Partial<AccountRegisterFormData> = {};
    Object.entries(DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA).forEach(([key, fallback]) => {
      const value = saved[key];
      const valid = Array.isArray(fallback)
        ? Array.isArray(value) && value.every(item => typeof item === "number" && Number.isFinite(item))
        : fallback === null
          ? value === null || (key === "job_industry"
            ? typeof value === "string"
            : typeof value === "number" && Number.isFinite(value))
          : typeof value === typeof fallback;
      if (valid) Object.assign(restored, { [key]: value });
    });
    if (typeof saved.has_investment_experience === "boolean") {
      restored.has_investment_experience = saved.has_investment_experience;
    }
    return restored;
  } catch (_) {
    return null;
  }
};

export const removeStorageAccountRegistrationState = (): void => {
  try {
    if (typeof window === "undefined" || typeof sessionStorage === "undefined") return;
    sessionStorage.removeItem(ACCOUNT_REGISTER_KEY);
    sessionStorage.removeItem(ACCOUNT_REGISTER_USER_ID_KEY);
  } catch (_) {
    // storageを使えない環境でも登録画面の操作を止めない。
  }
};

// 物件登録 - 保有物件 - 区分
export const setStoragePropertyRegistrationState = (value: AddNewPropertyFormSchema): void => {
  if (typeof window === "undefined") return;
  if (typeof localStorage === "undefined") return;
  localStorage.setItem("OWN_PROPERTY_REGISTER", JSON.stringify(value));
};

export const getStoragePropertyRegistrationState = (): AddNewPropertyFormSchema => {
  if (typeof window === "undefined") return;
  if (typeof localStorage === "undefined") return;
  return JSON.parse(localStorage.getItem("OWN_PROPERTY_REGISTER"));
};

export const removeStoragePropertyRegistrationState = (): void => {
  if (typeof window === "undefined") return;
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem("OWN_PROPERTY_REGISTER");
};

// 物件登録 - 検討中物件
export const setStorageConsiderationPropertyRegistrationState = (value: AddNewPropertyFormSchema): void => {
  if (typeof window === "undefined") return;
  if (typeof localStorage === "undefined") return;
  localStorage.setItem("CONSIDERATION_PROPERTY_REGISTER", JSON.stringify(value));
};

export const getStorageConsiderationPropertyRegistrationState = (): AddNewPropertyFormSchema => {
  if (typeof window === "undefined") return;
  if (typeof localStorage === "undefined") return;
  return JSON.parse(localStorage.getItem("CONSIDERATION_PROPERTY_REGISTER"));
};

export const removeStorageConsiderationPropertyRegistrationState = (): void => {
  if (typeof window === "undefined") return;
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem("CONSIDERATION_PROPERTY_REGISTER");
};

// 物件登録 - 保有物件 - 一棟
export const setStorageOwnBuildingPropertyRegistrationState = (value: AddNewPropertyFormSchema): void => {
  if (typeof window === "undefined") return;
  if (typeof localStorage === "undefined") return;
  localStorage.setItem("OWN_BUILDING_PROPERTY_REGISTER", JSON.stringify(value));
};

export const getStorageOwnBuildingPropertyRegistrationState = (): AddNewPropertyFormSchema => {
  if (typeof window === "undefined") return;
  if (typeof localStorage === "undefined") return;
  return JSON.parse(localStorage.getItem("OWN_BUILDING_PROPERTY_REGISTER"));
};

export const removeStorageOwnBuildingPropertyRegistrationState = (): void => {
  if (typeof window === "undefined") return;
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem("OWN_BUILDING_PROPERTY_REGISTER");
};
